import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Settings, Users, Trash2, Plus, Save, Upload, Image, Building2, ChevronRight, Pencil, UserMinus, Eye, EyeOff, RotateCcw, LogIn, X, Check, Lock, Unlock, AlertTriangle, Palette, Search, Shield, ShieldOff, CreditCard, Mail, BarChart3 } from "lucide-react";
import { adminAPI, companyLookupAPI } from "@/lib/api";
import { invalidatePaymentMethods } from "@/lib/paymentMethods";
import { CompanyLookup } from "@/components/CompanyLookup";
import TenantDetailPanel from "@/components/TenantDetailPanel";
import { toast } from "sonner";
import { countryCode } from "@/lib/legalIdentity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_MODULES = [
  { key: "fournisseurs", label: "Fournisseurs", desc: "Gestion fournisseurs, commandes, factures fournisseurs, devis" },
  { key: "marge", label: "Marge", desc: "Analyse de marge produit : import CSV Google Partner, matching clients, calcul marge revendeur" },
  { key: "ia", label: "IA", desc: "Analyse PDF par intelligence artificielle (Gemini) : extraction auto des factures, montants, TVA" },
  { key: "chatbot", label: "Chatbot IA", desc: "Assistant intelligent integre pour interroger vos donnees (CA, clients, factures) en langage naturel" },
  { key: "stats", label: "Statistiques", desc: "Tableaux de bord, KPIs, TVA collectee/deductible, graphiques evolution" },
  { key: "facturation_prev", label: "Facturation prev.", desc: "Factures à établir : contrats actifs, generation auto, relevé bancaire + rapprochement" },
  { key: "contrats", label: "Contrats", desc: "Contrats produits clients multi-lignes, suivi renouvellement, facturation récurrente" },
  { key: "email", label: "Email", desc: "Envoi d'emails depuis l'application (devis, factures, relances)" },
  { key: "marque_blanche", label: "Marque Blanche", desc: "Personnalisation du logiciel : couleurs, logo, nom de l'application" },
  { key: "paiement", label: "Paiement en ligne", desc: "Configuration Stripe et PayPal pour le paiement en ligne des factures" },
  { key: "emailing", label: "Marketing", desc: "Campagnes marketing vers des listes de clients et prospects avec suivi des envois" },
  { key: "export", label: "Export", desc: "Export CSV des documents et clients avec filtres" },
  { key: "comptabilite", label: "Comptabilité", desc: "Export et import FEC, report à nouveau pour la reprise comptable" },
];

const ROLE_LABELS = { owner: "Propriétaire", admin: "Administrateur", user: "Utilisateur" };
const ROLE_COLORS = { owner: "bg-amber-100 text-amber-700", admin: "bg-sky-100 text-sky-700", user: "bg-slate-100 text-slate-600" };

const DEFAULT_PAYMENT_METHODS = [
  { value: "virement", label: "Virement" },
  { value: "cheque", label: "Chèque" },
  { value: "carte", label: "Carte" },
  { value: "especes", label: "Espèces" },
  { value: "prelevement", label: "Prélèvement" },
  { value: "traite", label: "Traite" },
  { value: "autre", label: "Autre" },
];

const DEFAULT_PAYMENT_TERMS_LIST = [
  { value: "a_commande", label: "À la commande" },
  { value: "a_reception", label: "À réception de facture" },
  { value: "a_reception_marchandise", label: "À réception de marchandise" },
  { value: "au_depart", label: "Au départ marchandise" },
  { value: "a_livraison", label: "À la livraison" },
  { value: "15j", label: "15 jours" },
  { value: "30j", label: "30 jours" },
  { value: "30j_fdm", label: "30 jours fin de mois" },
  { value: "45j", label: "45 jours" },
  { value: "45j_fdm", label: "45 jours fin de mois" },
  { value: "60j", label: "60 jours" },
];

// ==================== COMPANY TAB ====================

// Types de documents paramétrables + presets par défaut (préservent le comportement historique F2K…)
const NUMBERING_DOC_TYPES = [
  { key: "devis", label: "Devis", legacy_prefix: "D2K" },
  { key: "commande", label: "Commande", legacy_prefix: "C2K" },
  { key: "facture", label: "Facture", legacy_prefix: "F2K" },
  { key: "avoir", label: "Avoir", legacy_prefix: "A2K" },
  { key: "proforma", label: "Proforma", legacy_prefix: "P2K" },
];

function NumberingSection({ form, setForm }) {
  const numbering = form.numbering || {};
  const update = (docType, field, value) => {
    setForm(prev => ({
      ...prev,
      numbering: {
        ...(prev.numbering || {}),
        [docType]: { ...(prev.numbering?.[docType] || {}), [field]: value },
      },
    }));
  };
  return (
    <div className="border-t pt-6 mt-6" data-testid="numbering-section">
      <div className="flex items-center gap-2 mb-2">
        <Settings size={18} className="text-indigo-600" />
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Numérotation des documents</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Configurez la numérotation pour reprendre une séquence existante (utile lors d&apos;une migration depuis un autre logiciel). Si aucun préfixe n&apos;est saisi, le format historique est conservé (ex. F2K26001).
      </p>
      <div className="responsive-table overflow-x-auto">
        <table className="w-full border-collapse text-sm min-w-[640px]">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Type</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Préfixe</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Chiffres</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Remise à zéro annuelle</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Prochain n°</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Aperçu</th>
            </tr>
          </thead>
          <tbody>
            {NUMBERING_DOC_TYPES.map(dt => {
              const cfg = numbering[dt.key] || {};
              const prefix = cfg.prefix || "";
              const digits = Math.max(1, parseInt(cfg.digits, 10) || 3);
              const yearlyReset = cfg.yearly_reset !== undefined ? !!cfg.yearly_reset : true;
              const nextNumber = Math.max(1, parseInt(cfg.next_number, 10) || 1);
              const yearSuffix = String(new Date().getFullYear()).slice(-2);
              const effectivePrefix = prefix ? (yearlyReset ? `${prefix}${yearSuffix}` : prefix) : `${dt.legacy_prefix}${yearSuffix}`;
              const preview = `${effectivePrefix}${String(nextNumber).padStart(digits, "0")}`;
              return (
                <tr key={dt.key} className="border-b" data-testid={`numbering-row-${dt.key}`}>
                  <td className="px-3 py-2 font-medium">{dt.label}</td>
                  <td className="px-3 py-2">
                    <Input value={prefix} onChange={e => update(dt.key, "prefix", e.target.value)} placeholder={dt.legacy_prefix} className="h-8 text-sm w-28" data-testid={`numbering-prefix-${dt.key}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min="1" max="10" value={cfg.digits ?? 3} onChange={e => update(dt.key, "digits", parseInt(e.target.value, 10) || 3)} className="h-8 text-sm w-20" data-testid={`numbering-digits-${dt.key}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Switch checked={yearlyReset} onCheckedChange={v => update(dt.key, "yearly_reset", !!v)} data-testid={`numbering-yearly-${dt.key}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min="1" value={cfg.next_number ?? 1} onChange={e => update(dt.key, "next_number", parseInt(e.target.value, 10) || 1)} className="h-8 text-sm w-24" data-testid={`numbering-next-${dt.key}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-indigo-700" data-testid={`numbering-preview-${dt.key}`}>{preview}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        Le prochain n° effectif est le max entre la valeur saisie et la séquence déjà atteinte par vos documents existants (imports compris).
      </p>
    </div>
  );
}

// ==================== COMPANY TAB ====================
function CompanyTab({ modules }) {
  const hasPaiement = (modules || []).includes("paiement");
  const [form, setForm] = useState({ name: "", address: "", city: "", postal_code: "", country: "France", siret: "", email: "", phone: "", workflow: "devis_facture", logo_url: "", regime_tva: "debits", bank_code: "", branch_code: "", account_number: "", rib_key: "", bank_name: "", iban: "", bic: "", website: "", legal_form: "", capital: "", rci: "", dsee: "", tva_number: "", address2: "", rcs_city: "", rcs_number: "", ape_code: "", email_template: "", fiscal_year_start_month: 1, accounting_method: "cash" });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [pmDraft, setPmDraft] = useState({ value: "", label: "" });
  const reloadSettings = () => { invalidatePaymentMethods(); return adminAPI.getSettings().then(r => setForm(prev => ({ ...prev, ...r.data }))); };
  const pmOptions = form.payment_method_options || DEFAULT_PAYMENT_METHODS;
  const addPaymentMethod = async () => {
    const value = (pmDraft.value || "").trim();
    if (!value) { toast.error("Indiquez un code de mode de règlement"); return; }
    try {
      await adminAPI.addPaymentMethod(value, pmDraft.label);
      await reloadSettings();
      setPmDraft({ value: "", label: "" });
      toast.success("Mode ajouté");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const renamePaymentLabel = async (value, label) => {
    try { await adminAPI.renamePaymentMethodLabel(value, label); await reloadSettings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const deletePaymentMethod = async (value) => {
    try { await adminAPI.deletePaymentMethod(value); await reloadSettings(); toast.success("Mode supprimé"); }
    catch (e) {
      const msg = e.response?.data?.detail || "Erreur";
      if (e.response?.status === 409 && window.confirm(msg + "\n\nSupprimer quand même ?")) {
        try { await adminAPI.deletePaymentMethod(value, true); await reloadSettings(); toast.success("Mode supprimé"); }
        catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); }
      } else { toast.error(msg); }
    }
  };
  const setDefaultPaymentMethod = async (value) => {
    try { await adminAPI.setDefaultPaymentMethod(value); await reloadSettings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  useEffect(() => {
    adminAPI.getSettings().then(r => { setForm(prev => ({ ...prev, ...r.data })); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    try { await adminAPI.updateSettings(form); toast.success("Parametres enregistrés"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadLogo(file);
      setForm(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo televerse");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <Label>Logo de l'entreprise</Label>
        <div className="mt-2 flex items-center gap-4">
          {form.logo_url ? (
            <div className="w-24 h-24 border rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
              <img src={`${BACKEND_URL}${form.logo_url}`} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="company-logo-preview" />
            </div>
          ) : (
            <div className="w-24 h-24 border-2 border-dashed rounded-lg bg-slate-50 flex items-center justify-center text-slate-400"><Image size={32} /></div>
          )}
          <div>
            <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp" onChange={handleLogoUpload} />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
              <Upload size={16} className="mr-2" />{uploading ? "Envoi..." : form.logo_url ? "Changer le logo" : "Televerser un logo"}
            </Button>
            <p className="text-xs text-slate-500 mt-1">PNG, JPG, SVG ou WEBP. Max 2 Mo</p>
          </div>
        </div>
      </div>
      <CompanyLookup onSelect={(r) => setForm(prev => ({ ...prev, name: r.nom || prev.name, siret: r.siret || prev.siret, address: r.adresse || prev.address, postal_code: r.code_postal || prev.postal_code, city: r.ville || prev.city }))} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Nom de la société *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="company-name-input" /></div>
        <div><Label>Forme juridique</Label><Input value={form.legal_form || ""} onChange={e => setForm({...form, legal_form: e.target.value})} data-testid="company-legal-form" placeholder="S.A.R.L. monégasque, SASU..." /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Adresse</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="company-address" /></div>
        <div><Label>Complément d&apos;adresse</Label><Input value={form.address2 || ""} onChange={e => setForm({...form, address2: e.target.value})} placeholder="Bâtiment, résidence..." data-testid="company-address2" /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><Label>Code postal</Label><Input value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} /></div>
        <div><Label>Ville</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
        <div>
          <Label>Pays</Label>
          <Select value={countryCode(form.country) === "MC" ? "Monaco" : "France"} onValueChange={v => setForm({...form, country: v})}>
            <SelectTrigger data-testid="company-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="France">France</SelectItem>
              <SelectItem value="Monaco">Monaco</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
        <div><Label>Téléphone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
      </div>
      <div>
        <Label>Adresse d&apos;expédition des documents</Label>
        <Input
          type="email"
          value={form.sender_email || ""}
          onChange={e => setForm({...form, sender_email: e.target.value})}
          placeholder="ex. factures@votre-domaine.com (laisser vide = adresse par défaut de la plateforme)"
          data-testid="company-sender-email"
        />
        <p className="text-xs text-slate-500 mt-1">
          Adresse « De : » utilisée pour l&apos;envoi des devis, proformas, commandes, factures et avoirs.
          Le domaine de cette adresse doit être vérifié chez Resend (enregistrements DNS SPF/DKIM sur <span className="font-mono">resend.com/domains</span>) ;
          sinon l&apos;email part depuis l&apos;adresse par défaut de la plateforme et un avertissement vous est affiché. Vide : adresse par défaut de la plateforme.
        </p>
      </div>
      <div><Label>RIB / Coordonnées bancaires</Label></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-xs text-slate-500">Code bancaire</Label><Input value={form.bank_code || ""} onChange={e => setForm({...form, bank_code: e.target.value})} data-testid="company-bank-code" /></div>
        <div><Label className="text-xs text-slate-500">Code guichet</Label><Input value={form.branch_code || ""} onChange={e => setForm({...form, branch_code: e.target.value})} data-testid="company-branch-code" /></div>
        <div><Label className="text-xs text-slate-500">N° de compte</Label><Input value={form.account_number || ""} onChange={e => setForm({...form, account_number: e.target.value})} data-testid="company-account-number" /></div>
        <div><Label className="text-xs text-slate-500">Clé RIB</Label><Input value={form.rib_key || ""} onChange={e => setForm({...form, rib_key: e.target.value})} data-testid="company-rib-key" /></div>
      </div>
      <div><Label className="text-xs text-slate-500">Domiciliation (banque)</Label><Input value={form.bank_name || ""} onChange={e => setForm({...form, bank_name: e.target.value})} data-testid="company-bank-name" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-500">IBAN</Label><Input value={form.iban || ""} onChange={e => setForm({...form, iban: e.target.value})} data-testid="company-iban" placeholder="FR76 ..." /></div>
        <div><Label className="text-xs text-slate-500">BIC / SWIFT</Label><Input value={form.bic || ""} onChange={e => setForm({...form, bic: e.target.value})} data-testid="company-bic" /></div>
      </div>
      <div><Label>Informations légales</Label>
        <p className="text-xs text-slate-500 mt-0.5">Ces mentions sont imprimées sous le logo, en haut de chaque document.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs text-slate-500">Site web</Label><Input value={form.website || ""} onChange={e => setForm({...form, website: e.target.value})} data-testid="company-website" placeholder="https://..." /></div>
        <div><Label className="text-xs text-slate-500">Capital social</Label><Input value={form.capital || ""} onChange={e => setForm({...form, capital: e.target.value})} data-testid="company-capital" placeholder="60 000" /></div>
      </div>
      {countryCode(form.country) === "MC" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">RCI</Label><Input value={form.rci || ""} onChange={e => setForm({...form, rci: e.target.value})} data-testid="company-rci" placeholder="10 S 05322" /></div>
          <div><Label className="text-xs text-slate-500">DSEE (NIS)</Label><Input value={form.dsee || ""} onChange={e => setForm({...form, dsee: e.target.value})} data-testid="company-dsee" placeholder="514 S 14049" /></div>
          <div><Label className="text-xs text-slate-500">N° TVA intracom.</Label><Input value={form.tva_number || ""} onChange={e => setForm({...form, tva_number: e.target.value})} data-testid="company-tva-number" placeholder="FR 51 00008896 2" /></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs text-slate-500">SIRET</Label><Input value={form.siret || ""} onChange={e => setForm({...form, siret: e.target.value})} data-testid="company-siret-input" placeholder="123 456 789 00012" /></div>
            <div><Label className="text-xs text-slate-500">RCS — ville</Label><Input value={form.rcs_city || ""} onChange={e => setForm({...form, rcs_city: e.target.value})} data-testid="company-rcs-city" placeholder="Nice" /></div>
            <div><Label className="text-xs text-slate-500">RCS — numéro</Label><Input value={form.rcs_number || ""} onChange={e => setForm({...form, rcs_number: e.target.value})} data-testid="company-rcs-number" placeholder="818 769 614" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Code APE</Label><Input value={form.ape_code || ""} onChange={e => setForm({...form, ape_code: e.target.value})} data-testid="company-ape-code" placeholder="6201Z" /></div>
            <div><Label className="text-xs text-slate-500">N° TVA intracom.</Label><Input value={form.tva_number || ""} onChange={e => setForm({...form, tva_number: e.target.value})} data-testid="company-tva-number" placeholder="FR 00 818769614" /></div>
          </div>
        </>
      )}
      <div>
        <Label>Regime de TVA</Label>
        <Select value={form.regime_tva || "encaissements"} onValueChange={v => setForm({...form, regime_tva: v})}>
          <SelectTrigger data-testid="regime-tva-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="encaissements">TVA sur les encaissements (prestations de services)</SelectItem>
            <SelectItem value="debits">TVA sur les debits (vente de biens)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Workflow commercial</Label>
        <Select value={form.workflow} onValueChange={v => setForm({...form, workflow: v})}>
          <SelectTrigger data-testid="workflow-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="devis_facture">Devis - Facture</SelectItem>
            <SelectItem value="proforma_commande_facture">Devis - Commande - Facture</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* Exercice comptable — utilisé par Compte de résultat, Dashboard, FEC */}
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Exercice comptable</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Définit la période sur laquelle votre Compte de résultat et vos rapports financiers sont calculés.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Début d&apos;exercice fiscal</Label>
            <Select value={String(form.fiscal_year_start_month || 1)} onValueChange={v => setForm({...form, fiscal_year_start_month: Number(v)})}>
              <SelectTrigger data-testid="fiscal-year-start-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1ᵉʳ janvier (année civile)</SelectItem>
                <SelectItem value="2">1ᵉʳ février</SelectItem>
                <SelectItem value="3">1ᵉʳ mars</SelectItem>
                <SelectItem value="4">1ᵉʳ avril</SelectItem>
                <SelectItem value="5">1ᵉʳ mai</SelectItem>
                <SelectItem value="6">1ᵉʳ juin</SelectItem>
                <SelectItem value="7">1ᵉʳ juillet</SelectItem>
                <SelectItem value="8">1ᵉʳ août</SelectItem>
                <SelectItem value="9">1ᵉʳ septembre</SelectItem>
                <SelectItem value="10">1ᵉʳ octobre</SelectItem>
                <SelectItem value="11">1ᵉʳ novembre</SelectItem>
                <SelectItem value="12">1ᵉʳ décembre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Méthode comptable par défaut</Label>
            <Select value={form.accounting_method || "cash"} onValueChange={v => setForm({...form, accounting_method: v})}>
              <SelectTrigger data-testid="accounting-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Trésorerie (encaissements / décaissements)</SelectItem>
                <SelectItem value="accrual">Engagement (toutes les factures, payées ou non)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-1">
              Vous pourrez basculer ce choix au cas par cas sur le Compte de résultat.
            </p>
          </div>
        </div>
      </div>
      <div>
        <Label>Marge par defaut (%)</Label>
        <Input type="number" step="0.1" value={form.default_margin_percent || ""} onChange={e => setForm({...form, default_margin_percent: parseFloat(e.target.value) || 0})} placeholder="20" data-testid="default-margin-input" className="max-w-[200px]" />
      </div>
      {/* ===== Numérotation des documents (paramétrable pour migrations) ===== */}
      <NumberingSection form={form} setForm={setForm} />
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Conditions de paiement</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Gerez les options de conditions de paiement proposees sur vos documents (devis, factures, etc.).</p>
        <div className="space-y-2 mb-3">
          {(form.payment_terms_options || DEFAULT_PAYMENT_TERMS_LIST).map((pt, idx) => (
            <div key={pt.value || `pt-${idx}`} className="flex items-center gap-2">
              <Input value={pt.label} onChange={e => {
                const opts = [...(form.payment_terms_options || DEFAULT_PAYMENT_TERMS_LIST)];
                opts[idx] = { ...opts[idx], label: e.target.value };
                setForm({...form, payment_terms_options: opts});
              }} className="h-8 text-sm flex-1" data-testid={`payment-term-${idx}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => {
                const opts = [...(form.payment_terms_options || DEFAULT_PAYMENT_TERMS_LIST)].filter((_, i) => i !== idx);
                setForm({...form, payment_terms_options: opts});
              }}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          const opts = [...(form.payment_terms_options || DEFAULT_PAYMENT_TERMS_LIST)];
          const newVal = `custom_${Date.now()}`;
          opts.push({ value: newVal, label: "" });
          setForm({...form, payment_terms_options: opts});
        }} data-testid="add-payment-term-btn"><Plus size={14} className="mr-1" />Ajouter une option</Button>
        <div className="mt-3">
          <Label className="text-xs">Condition par defaut</Label>
          <Select value={form.default_payment_terms || "none"} onValueChange={v => setForm({...form, default_payment_terms: v === "none" ? "" : v})}>
            <SelectTrigger className="max-w-xs" data-testid="default-payment-term"><SelectValue placeholder="Aucune" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune</SelectItem>
              {(form.payment_terms_options || DEFAULT_PAYMENT_TERMS_LIST).map(pt => (
                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Modes de règlement</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Liste proposée à la création des documents. Le <strong>code</strong> est immuable une fois créé (il identifie la valeur stockée) ; seul le <strong>libellé</strong> affiché se renomme. Une valeur absente de cette liste reste affichée telle quelle sur les documents.</p>
        <div className="space-y-2" data-testid="payment-method-list">
          {pmOptions.map((pm) => (
            <div key={pm.value} className="flex items-center gap-2" data-testid={`payment-method-row-${pm.value}`}>
              <span className="text-xs font-mono text-slate-400 w-28 shrink-0 truncate" title={`code : ${pm.value}`}>{pm.value}</span>
              <Input
                value={pm.label}
                onChange={e => setForm(prev => ({ ...prev, payment_method_options: (prev.payment_method_options || DEFAULT_PAYMENT_METHODS).map(x => x.value === pm.value ? { ...x, label: e.target.value } : x) }))}
                onBlur={e => renamePaymentLabel(pm.value, e.target.value)}
                className="max-w-xs h-8 text-sm"
                data-testid={`payment-method-label-${pm.value}`}
              />
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deletePaymentMethod(pm.value)} data-testid={`payment-method-delete-${pm.value}`}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 mt-3">
          <div>
            <Label className="text-xs">Code</Label>
            <Input value={pmDraft.value} onChange={e => setPmDraft(d => ({ ...d, value: e.target.value }))} placeholder="ex. paypal" className="h-8 text-sm w-32" data-testid="payment-method-new-value" />
          </div>
          <div>
            <Label className="text-xs">Libellé</Label>
            <Input value={pmDraft.label} onChange={e => setPmDraft(d => ({ ...d, label: e.target.value }))} placeholder="ex. PayPal" className="h-8 text-sm w-40" data-testid="payment-method-new-label" />
          </div>
          <Button variant="outline" size="sm" onClick={addPaymentMethod} data-testid="payment-method-add-btn"><Plus size={14} className="mr-1" />Ajouter</Button>
        </div>
        <div className="mt-3">
          <Label className="text-xs">Mode par défaut</Label>
          <Select value={form.default_payment_method || "none"} onValueChange={v => setDefaultPaymentMethod(v === "none" ? "" : v)}>
            <SelectTrigger className="max-w-xs" data-testid="default-payment-method"><SelectValue placeholder="Aucun" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              {pmOptions.map(pm => (<SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <RotateCcw size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Modalité d&apos;avoir par défaut</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Pré-remplie à la création de chaque avoir, et modifiable au cas par cas. La modalité est obligatoire avant l&apos;émission d&apos;un avoir : un avoir muet ne doit pas partir chez un client.</p>
        <Select value={form.default_avoir_modality || "none"} onValueChange={v => setForm({...form, default_avoir_modality: v === "none" ? "" : v})}>
          <SelectTrigger className="max-w-md" data-testid="default-avoir-modality"><SelectValue placeholder="Aucune" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucune (à saisir à chaque fois)</SelectItem>
            <SelectItem value="imputation">Imputation sur la facture d&apos;origine</SelectItem>
            <SelectItem value="remboursement">Remboursement par virement sous 30 jours</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Conditions de vente</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Ce texte sera pre-rempli sur chaque nouveau document. Il reste modifiable au cas par cas.</p>
        <textarea
          value={form.sales_conditions || ""}
          onChange={e => setForm({...form, sales_conditions: e.target.value})}
          rows={5}
          className="w-full text-sm rounded-md border border-slate-200 p-3 resize-y"
          data-testid="sales-conditions-input"
          placeholder="Conditions générales de vente..."
        />
      </div>
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Template Email par defaut</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Ce texte sera utilise comme contenu par defaut lors de l'envoi de documents par email. Variables disponibles : <code className="bg-slate-100 px-1 rounded text-[11px]">{'{doc_label}'}</code> <code className="bg-slate-100 px-1 rounded text-[11px]">{'{doc_number}'}</code> <code className="bg-slate-100 px-1 rounded text-[11px]">{'{doc_date}'}</code> <code className="bg-slate-100 px-1 rounded text-[11px]">{'{client_name}'}</code> <code className="bg-slate-100 px-1 rounded text-[11px]">{'{company_name}'}</code> <code className="bg-slate-100 px-1 rounded text-[11px]">{'{total_ttc}'}</code> <code className="bg-slate-100 px-1 rounded text-[11px]">{'{reste_a_payer}'}</code></p>
        <p className="text-xs text-slate-500 mb-3"><code className="bg-slate-100 px-1 rounded text-[11px]">{'{total_ttc}'}</code> vaut toujours le total TTC du document. <code className="bg-slate-100 px-1 rounded text-[11px]">{'{reste_a_payer}'}</code> vaut le reste dû après avoirs imputés et règlements. Si votre modèle ne contient pas <code className="bg-slate-100 px-1 rounded text-[11px]">{'{reste_a_payer}'}</code>, un récapitulatif de règlement est ajouté automatiquement sous votre texte lorsque la facture porte un avoir imputé ou un règlement partiel.</p>
        <textarea
          value={form.email_template || ""}
          onChange={e => setForm({...form, email_template: e.target.value})}
          rows={6}
          className="w-full text-sm rounded-md border border-slate-200 p-3 resize-y"
          data-testid="email-template-input"
          placeholder={"Bonjour,\n\nVeuillez trouver ci-joint votre {doc_label} n°{doc_number} du {doc_date}.\n\nNous restons a votre disposition pour toute question.\n\nCordialement,"}
        />
      </div>
      {/* Payment API Configuration */}
      {hasPaiement && (
      <div className="border-t pt-6 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard size={18} className="text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Configuration Paiement</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Configurez vos cles API pour permettre le paiement en ligne sur vos factures.</p>
        <div className="space-y-4">
          <div className="p-4 border rounded-lg bg-slate-50/50">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-slate-700">Stripe</span>
              <Badge variant={form.stripe_secret_key ? "default" : "secondary"} className="text-[10px]">{form.stripe_secret_key ? "Configure" : "Non configure"}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cle publique (pk_...)</Label>
                <Input value={form.stripe_public_key || ""} onChange={e => setForm({...form, stripe_public_key: e.target.value})} placeholder="pk_live_..." type="password" data-testid="stripe-public-key-input" />
              </div>
              <div>
                <Label className="text-xs">Cle secrete (sk_...)</Label>
                <Input value={form.stripe_secret_key || ""} onChange={e => setForm({...form, stripe_secret_key: e.target.value})} placeholder="sk_live_..." type="password" data-testid="stripe-secret-key-input" />
              </div>
            </div>
          </div>
          <div className="p-4 border rounded-lg bg-slate-50/50">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-slate-700">PayPal</span>
              <Badge variant={form.paypal_client_id ? "default" : "secondary"} className="text-[10px]">{form.paypal_client_id ? "Configure" : "Non configure"}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Client ID</Label>
                <Input value={form.paypal_client_id || ""} onChange={e => setForm({...form, paypal_client_id: e.target.value})} placeholder="AX..." type="password" data-testid="paypal-client-id-input" />
              </div>
              <div>
                <Label className="text-xs">Client Secret</Label>
                <Input value={form.paypal_secret || ""} onChange={e => setForm({...form, paypal_secret: e.target.value})} placeholder="EL..." type="password" data-testid="paypal-secret-input" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Email PayPal (pour le lien de paiement)</Label>
              <Input value={form.paypal_email || ""} onChange={e => setForm({...form, paypal_email: e.target.value})} placeholder="paiement@masociété.fr" type="email" data-testid="paypal-email-input" />
            </div>
          </div>
        </div>
      </div>
      )}
      <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-company-btn">
        <Save size={16} className="mr-2" /> Enregistrer
      </Button>
    </div>
  );
}

// ==================== MODULES TAB (for tenant admin) ====================
function ModulesTab({ user }) {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [importEnabled, setImportEnabled] = useState(false);
  const isSuperadmin = user?.is_superadmin || false;
  const isAdmin = isSuperadmin || user?.tenant_role === "owner" || user?.tenant_role === "admin";
  const tenantId = user?.current_tenant_id;

  useEffect(() => {
    adminAPI.getMyTenant().then(r => {
      setTenantInfo(r.data);
      setModules(r.data?.modules || []);
      setImportEnabled(r.data?.import_enabled || false);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleModule = (key) => {
    setModules(prev => prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]);
  };

  const saveModules = async () => {
    if (!tenantId) return;
    try {
      await adminAPI.updateTenantModules(tenantId, modules);
      toast.success("Modules mis à jour — rafraîchissement...");
      setTimeout(() => window.location.reload(), 500);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;
  if (!tenantInfo) return null;

  const activeModules = isSuperadmin ? modules : (tenantInfo.modules || []);

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-slate-500 mb-4">
        {isSuperadmin ? "Activez ou desactivez les modules pour cette société." : "Modules actifs pour votre société. Contactez votre administrateur pour activer de nouveaux modules."}
      </p>
      <div className="space-y-2">
        {ALL_MODULES.map(m => {
          const isActive = activeModules.includes(m.key);
          return (
            <div key={m.key}
              onClick={isSuperadmin ? () => toggleModule(m.key) : undefined}
              className={`flex items-center gap-4 p-3.5 rounded-lg border transition-colors ${isSuperadmin ? "cursor-pointer hover:border-indigo-300" : ""} ${isActive ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50/50 opacity-60"}`}
              data-testid={`module-view-${m.key}`}>
              {isSuperadmin ? (
                <input type="checkbox" checked={isActive} onChange={() => toggleModule(m.key)} onClick={e => e.stopPropagation()} className="rounded border-slate-300 w-5 h-5 text-indigo-600" data-testid={`module-toggle-${m.key}`} />
              ) : (
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? "bg-emerald-100" : "bg-slate-200"}`}>
                  {isActive ? <Unlock size={16} className="text-emerald-600" /> : <Lock size={16} className="text-slate-400" />}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-700">{m.label}</p>
                  {isActive ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Actif</Badge>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-500 text-[10px]">Non inclus</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{m.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
      {isAdmin && (
        <div className="mt-6 pt-4 border-t border-slate-200">
          <div
            onClick={async () => {
              const next = !importEnabled;
              try {
                await adminAPI.toggleImport(next);
                setImportEnabled(next);
                toast.success(next ? "Import active — rafraichissement..." : "Import desactive — rafraichissement...");
                setTimeout(() => window.location.reload(), 500);
              } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
            }}
            className={`flex items-center gap-4 p-3.5 rounded-lg border transition-colors cursor-pointer hover:border-indigo-300 ${importEnabled ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50/50 opacity-60"}`}
            data-testid="toggle-import"
          >
            <input type="checkbox" checked={importEnabled} readOnly className="rounded border-slate-300 w-5 h-5 text-indigo-600" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-slate-700">Import de donnees</p>
                {importEnabled ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Actif</Badge>
                ) : (
                  <Badge className="bg-slate-200 text-slate-500 text-[10px]">Desactive</Badge>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Affiche le menu Import dans la barre laterale. A activer uniquement lors de la migration initiale des donnees.</p>
            </div>
          </div>
        </div>
      )}
      {isSuperadmin && (
        <Button onClick={saveModules} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-modules-direct-btn">
          <Save size={16} className="mr-2" /> Enregistrer les modules
        </Button>
      )}
    </div>
  );
}

// ==================== TENANT USERS TAB (for tenant admin) ====================
function TenantUsersTab({ user }) {
  const [tenantUsers, setTenantUsers] = useState([]);
  const [maxUsers, setMaxUsers] = useState(0);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: "", password: "", name: "", role_in_tenant: "user" });
  const [showPw, setShowPw] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });
  const [showEditPw, setShowEditPw] = useState(false);
  const tenantId = user?.current_tenant_id;

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const r = await adminAPI.listTenantUsers(tenantId);
      setTenantUsers(r.data.users || r.data);
      setMaxUsers(r.data.max_users || 0);
    } catch { setTenantUsers([]); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const addUser = async () => {
    if (!addUserForm.email || !addUserForm.password) { toast.error("Email et mot de passe requis"); return; }
    try {
      await adminAPI.createTenantUser(tenantId, addUserForm);
      toast.success("Utilisateur ajouté");
      setShowAddUser(false);
      setAddUserForm({ email: "", password: "", name: "", role_in_tenant: "user" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const updateRole = async (userId, role) => {
    try {
      await adminAPI.updateTenantUserRole(tenantId, userId, role);
      toast.success("Rôle mis à jour");
      setEditingRole(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const startEditUser = (u) => {
    setEditingUser(u.user_id);
    setEditForm({ name: u.name || "", email: u.email || "", password: "" });
    setShowEditPw(false);
  };

  const saveEditUser = async (userId) => {
    try {
      const data = { name: editForm.name, email: editForm.email };
      if (editForm.password) data.password = editForm.password;
      await adminAPI.updateUser(userId, data);
      toast.success("Utilisateur mis à jour");
      setEditingUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeUser = async (userId, name) => {
    if (!window.confirm(`Retirer ${name} de cette société ?`)) return;
    try {
      await adminAPI.removeTenantUser(tenantId, userId);
      toast.success("Utilisateur retiré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const limitReached = maxUsers > 0 && tenantUsers.length >= maxUsers;

  return (
    <div className="max-w-2xl">
      <div className="flex justify-between items-center mb-4">
        <div>
          <p className="text-sm text-slate-500">{tenantUsers.length} utilisateur(s){maxUsers > 0 ? ` / ${maxUsers} max` : ""}</p>
          {limitReached && (
            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1"><AlertTriangle size={12} /> Limite atteinte. Contactez votre administrateur.</p>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAddUser(true)} disabled={limitReached} data-testid="add-user-btn">
          <Plus size={14} className="mr-1" />Ajouter
        </Button>
      </div>

      {showAddUser && (
        <Card className="p-4 border-indigo-200 bg-indigo-50/30 space-y-3 mb-4" data-testid="add-user-form">
          <p className="text-sm font-medium text-indigo-700">Nouvel utilisateur</p>
          <div><Label className="text-xs">Nom</Label><Input value={addUserForm.name} onChange={e => setAddUserForm({...addUserForm, name: e.target.value})} placeholder="Jean Dupont" data-testid="new-user-name" /></div>
          <div><Label className="text-xs">Email *</Label><Input type="email" value={addUserForm.email} onChange={e => setAddUserForm({...addUserForm, email: e.target.value})} placeholder="jean@exemple.fr" data-testid="new-user-email" /></div>
          <div>
            <Label className="text-xs">Mot de passe *</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} value={addUserForm.password} onChange={e => setAddUserForm({...addUserForm, password: e.target.value})} placeholder="Min. 6 caractères" data-testid="new-user-password" />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={addUserForm.role_in_tenant} onValueChange={v => setAddUserForm({...addUserForm, role_in_tenant: v})}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrateur</SelectItem>
                <SelectItem value="user">Utilisateur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={addUser} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-add-user-btn">Ajouter</Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddUser(false)}>Annuler</Button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {tenantUsers.map(u => (
          <div key={u.user_id} className="border border-slate-200 rounded-lg bg-white" data-testid={`tenant-user-${u.user_id}`}>
            <div className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3">
                {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : (
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">{(u.name || u.email)?.[0]?.toUpperCase()}</div>
                )}
                <div>
                  <p className="text-sm font-medium">{u.name || u.email}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {editingRole === u.user_id ? (
                  <Select defaultValue={u.role_in_tenant} onValueChange={v => updateRole(u.user_id, v)}>
                    <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrateur</SelectItem>
                      <SelectItem value="user">Utilisateur</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`${ROLE_COLORS[u.role_in_tenant] || ROLE_COLORS.user} text-xs cursor-pointer`}
                    onClick={() => u.user_id !== user?.user_id && setEditingRole(u.user_id)}
                    data-testid={`role-badge-${u.user_id}`}>
                    {ROLE_LABELS[u.role_in_tenant] || "Utilisateur"}
                  </Badge>
                )}
                {u.user_id !== user?.user_id && (
                  <>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => editingUser === u.user_id ? setEditingUser(null) : startEditUser(u)} data-testid={`edit-user-${u.user_id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 h-7 w-7" onClick={() => removeUser(u.user_id, u.name || u.email)} data-testid={`remove-user-${u.user_id}`}>
                      <UserMinus size={14} />
                    </Button>
                  </>
                )}
              </div>
            </div>
            {editingUser === u.user_id && (
              <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50/50" data-testid={`edit-user-form-${u.user_id}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Nom</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="h-8 text-sm" data-testid="edit-user-name" /></div>
                  <div><Label className="text-xs">Email</Label><Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="h-8 text-sm" data-testid="edit-user-email" /></div>
                  <div>
                    <Label className="text-xs">Nouveau mot de passe</Label>
                    <div className="relative">
                      <Input type={showEditPw ? "text" : "password"} value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} placeholder="Laisser vide" className="h-8 text-sm pr-8" data-testid="edit-user-password" />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowEditPw(!showEditPw)}>{showEditPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => saveEditUser(u.user_id)} className="bg-slate-900 hover:bg-slate-800 text-white h-7 text-xs" data-testid="save-user-btn"><Check size={14} className="mr-1" />Enregistrer</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingUser(null)} className="h-7 text-xs">Annuler</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ==================== TENANTS TAB (superadmin) ====================
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", owner_email: "", owner_password: "", workflow: "devis_facture", modules: ALL_MODULES.map(m => m.key), max_users: 0 });
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState(false);

  const load = useCallback(() => {
    adminAPI.listTenants().then(r => { setTenants(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshAndUpdate = () => {
    adminAPI.listTenants().then(r => {
      setTenants(r.data);
      if (selectedTenant) {
        const updated = r.data.find(t => t.id === selectedTenant.id);
        setSelectedTenant(updated || null);
      }
    });
  };

  const createTenant = async () => {
    if (!form.name || !form.slug) { toast.error("Nom et slug requis"); return; }
    try {
      await adminAPI.createTenant(form);
      toast.success("Société créée");
      setShowCreateDialog(false);
      setForm({ name: "", slug: "", owner_email: "", owner_password: "", workflow: "devis_facture", modules: ALL_MODULES.map(m => m.key), max_users: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleFormModule = (key) => setForm(prev => ({ ...prev, modules: prev.modules.includes(key) ? prev.modules.filter(m => m !== key) : [...prev.modules, key] }));

  return (
    <div className="flex" style={{ minHeight: "500px" }}>
      <div className={`${selectedTenant ? "w-1/2 lg:w-2/5" : "w-full"} transition-all`}>
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-slate-500">{tenants.length} société(s)</p>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="create-tenant-btn">
            <Plus size={16} className="mr-2" /> Nouvelle société
          </Button>
        </div>
        <div className="space-y-2">
          {tenants.map(t => (
            <div key={t.id}
              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${selectedTenant?.id === t.id ? "border-indigo-300 bg-indigo-50/50 ring-1 ring-indigo-200" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              onClick={() => setSelectedTenant(t)} data-testid={`tenant-row-${t.slug}`}>
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${t.active ? "bg-emerald-500" : "bg-red-400"}`} />
                <div>
                  <p className="font-medium text-sm">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.slug} &middot; {t.user_count || 0} utilisateur(s){t.max_users > 0 ? ` / ${t.max_users}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{(t.modules || []).length}/{ALL_MODULES.length}</Badge>
                <ChevronRight size={16} className="text-slate-300" />
              </div>
            </div>
          ))}
          {tenants.length === 0 && <p className="text-center text-slate-400 py-8">{loading ? "Chargement..." : "Aucune société"}</p>}
        </div>
      </div>
      {selectedTenant && (
        <div className="w-1/2 lg:w-3/5 ml-4">
          <TenantDetailPanel tenant={selectedTenant} onClose={() => setSelectedTenant(null)} onRefresh={refreshAndUpdate} />
        </div>
      )}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle société</DialogTitle><DialogDescription>Créez un espace isole pour un nouveau client.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-")})} placeholder="Ma Société" data-testid="tenant-name-input" /></div>
            <div><Label>Slug *</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")})} placeholder="ma-société" data-testid="tenant-slug-input" /><p className="text-xs text-slate-500 mt-1">Base: crm_tenant_{form.slug || "..."}</p></div>
            <div><Label>Email proprietaire</Label><Input type="email" value={form.owner_email} onChange={e => setForm({...form, owner_email: e.target.value})} placeholder="owner@exemple.fr" data-testid="tenant-owner-input" /></div>
            <div>
              <Label>Mot de passe proprietaire</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} value={form.owner_password} onChange={e => setForm({...form, owner_password: e.target.value})} placeholder="Min. 6 caractères" data-testid="tenant-owner-password-input" />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div><Label>Limite d'utilisateurs</Label><Input type="number" min="0" value={form.max_users} onChange={e => setForm({...form, max_users: parseInt(e.target.value) || 0})} data-testid="tenant-max-users-input" /><p className="text-xs text-slate-500 mt-1">0 = illimite</p></div>
            <div>
              <Label>Workflow commercial</Label>
              <Select value={form.workflow} onValueChange={v => setForm({...form, workflow: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="devis_facture">Devis - Facture</SelectItem>
                  <SelectItem value="proforma_commande_facture">Devis - Commande - Facture</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Modules</Label>
              <div className="space-y-2">
                {ALL_MODULES.map(m => (
                  <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.modules.includes(m.key)} onChange={() => toggleFormModule(m.key)} className="rounded border-slate-300" data-testid={`module-create-${m.key}`} />
                    <span className="text-sm">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={createTenant} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="confirm-create-tenant-btn">Créer la société</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== USERS TAB (superadmin view) ====================
function AllUsersTab() {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);

  const load = useCallback(() => { adminAPI.listUsers().then(r => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (u) => { setEditingUser(u.user_id); setEditForm({ name: u.name || "", email: u.email || "", password: "" }); setShowPw(false); };

  const saveEdit = async (userId) => {
    try {
      const data = { name: editForm.name, email: editForm.email };
      if (editForm.password) data.password = editForm.password;
      await adminAPI.updateUser(userId, data);
      toast.success("Mis à jour");
      setEditingUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "user" : "admin";
    try {
      await adminAPI.updateUserRole(u.user_id, newRole);
      toast.success(`Role global: ${newRole === "admin" ? "Super-admin" : "Utilisateur"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer definitivement ${u.name || u.email} ? Cette action est irreversible.`)) return;
    try {
      await adminAPI.deleteUser(u.user_id);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-slate-500 mb-4">{users.length} utilisateur(s) au total</p>
      <div className="space-y-2">
        {users.map(u => (
          <div key={u.user_id} className="border border-slate-200 rounded-lg bg-white" data-testid={`user-row-${u.user_id}`}>
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full" /> : (
                  <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-600">{(u.name || u.email)?.[0]?.toUpperCase()}</div>
                )}
                <div>
                  <p className="font-medium text-sm">{u.name || "-"}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {u.memberships && (
                  <div className="flex flex-wrap gap-1">
                    {u.memberships.map(m => (
                      <Badge key={m.tenant_id} className="bg-slate-100 text-slate-600 text-[10px]">
                        {m.tenant_name} ({ROLE_LABELS[m.role_in_tenant] || m.role_in_tenant})
                      </Badge>
                    ))}
                    {u.memberships.length === 0 && u.role !== "admin" && (
                      <Badge className="bg-red-50 text-red-500 text-[10px]">Aucune société</Badge>
                    )}
                  </div>
                )}
                <Button variant="ghost" size="sm" className={`h-7 text-xs gap-1 ${u.role === "admin" ? "text-indigo-600" : "text-slate-500"}`} onClick={() => toggleRole(u)} title={u.role === "admin" ? "Retirer super-admin" : "Promouvoir super-admin"} data-testid={`toggle-role-${u.user_id}`}>
                  {u.role === "admin" ? <Shield size={14} /> : <ShieldOff size={14} />}
                  {u.role === "admin" ? "Super-admin" : "User"}
                </Button>
                <div className="text-xs text-slate-400 w-16 text-right">
                  {u.last_login ? new Date(u.last_login).toLocaleDateString("fr-FR") : "-"}
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => editingUser === u.user_id ? setEditingUser(null) : startEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                  <Pencil size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.user_id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            {editingUser === u.user_id && (
              <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50/50" data-testid={`edit-user-form-${u.user_id}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Nom</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="h-8 text-sm" /></div>
                  <div><Label className="text-xs">Email</Label><Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="h-8 text-sm" /></div>
                  <div>
                    <Label className="text-xs">Nouveau mot de passe</Label>
                    <div className="relative">
                      <Input type={showPw ? "text" : "password"} value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} placeholder="Laisser vide" className="h-8 text-sm pr-8" />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => saveEdit(u.user_id)} className="bg-slate-900 hover:bg-slate-800 text-white h-7 text-xs"><Check size={14} className="mr-1" />Enregistrer</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingUser(null)} className="h-7 text-xs">Annuler</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== BRANDING TAB (marque blanche) ====================
const BRANDING_DEFAULTS = { primary_color: "#1e293b", accent_color: "#4f46e5", sidebar_color: "#0f172a", app_name: "", logo_url: "" };

function BrandingTab() {
  const [branding, setBranding] = useState({ ...BRANDING_DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    adminAPI.getMyTenant().then(r => {
      const b = r.data?.branding || {};
      setBranding(prev => ({ ...prev, ...b }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      await adminAPI.updateBranding(branding);
      toast.success("Personnalisation enregistrée. Rechargez la page pour voir les changements.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const resetAll = async () => {
    if (!window.confirm("Revenir aux parametres par defaut ? Toute personnalisation sera perdue.")) return;
    setBranding({ ...BRANDING_DEFAULTS });
    try {
      await adminAPI.updateBranding(BRANDING_DEFAULTS);
      toast.success("Personnalisation reinitialisee. Rechargez la page.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeLogo = async () => {
    setBranding(prev => ({ ...prev, logo_url: "" }));
    try {
      await adminAPI.updateBranding({ ...branding, logo_url: "" });
      toast.success("Logo supprime. Rechargez la page.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const resetColors = () => {
    setBranding(prev => ({ ...prev, primary_color: BRANDING_DEFAULTS.primary_color, accent_color: BRANDING_DEFAULTS.accent_color, sidebar_color: BRANDING_DEFAULTS.sidebar_color }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadBrandingLogo(file);
      setBranding(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo televerse");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };

  const isDefault = branding.primary_color === BRANDING_DEFAULTS.primary_color && branding.accent_color === BRANDING_DEFAULTS.accent_color && branding.sidebar_color === BRANDING_DEFAULTS.sidebar_color && !branding.app_name && !branding.logo_url;

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
        <p className="text-sm text-amber-800">Personnalisez l'apparence du logiciel pour votre société. Les modifications s'appliquent a tous les utilisateurs de votre espace.</p>
      </div>

      <div>
        <Label>Nom de l'application</Label>
        <div className="flex gap-2 mt-1">
          <Input value={branding.app_name} onChange={e => setBranding({...branding, app_name: e.target.value})} placeholder="KomptAI (par defaut)" className="max-w-sm" data-testid="branding-app-name" />
          {branding.app_name && <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400" onClick={() => setBranding({...branding, app_name: ""})} data-testid="clear-app-name"><X size={16} /></Button>}
        </div>
        <p className="text-xs text-slate-500 mt-1">Laissez vide pour utiliser le nom par defaut</p>
      </div>

      <div>
        <Label>Logo personnalise</Label>
        <div className="mt-2 flex items-center gap-4">
          {branding.logo_url ? (
            <div className="w-24 h-24 border rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center relative group">
              <img src={`${BACKEND_URL}${branding.logo_url}`} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="branding-logo-preview" />
            </div>
          ) : (
            <div className="w-24 h-24 border-2 border-dashed rounded-lg bg-slate-50 flex items-center justify-center text-slate-400"><Image size={32} /></div>
          )}
          <div className="space-y-2">
            <input type="file" ref={fileInputRef} className="hidden" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-branding-logo-btn">
              <Upload size={16} className="mr-2" />{uploading ? "Envoi..." : branding.logo_url ? "Changer le logo" : "Televerser un logo"}
            </Button>
            {branding.logo_url && (
              <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50" onClick={removeLogo} data-testid="remove-branding-logo-btn">
                <Trash2 size={14} className="mr-1" />Supprimer le logo
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Couleurs</Label>
          {(branding.primary_color !== BRANDING_DEFAULTS.primary_color || branding.accent_color !== BRANDING_DEFAULTS.accent_color || branding.sidebar_color !== BRANDING_DEFAULTS.sidebar_color) && (
            <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={resetColors} data-testid="reset-colors-btn">
              <RotateCcw size={14} className="mr-1" />Couleurs par defaut
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-500">Couleur principale</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={branding.primary_color} onChange={e => setBranding({...branding, primary_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer border border-slate-200" data-testid="branding-primary-color" />
              <Input value={branding.primary_color} onChange={e => setBranding({...branding, primary_color: e.target.value})} className="h-8 text-sm font-mono flex-1" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Boutons, liens, accents</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Couleur d'accentuation</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={branding.accent_color} onChange={e => setBranding({...branding, accent_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer border border-slate-200" data-testid="branding-accent-color" />
              <Input value={branding.accent_color} onChange={e => setBranding({...branding, accent_color: e.target.value})} className="h-8 text-sm font-mono flex-1" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Badges, icones actives</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Sidebar</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={branding.sidebar_color} onChange={e => setBranding({...branding, sidebar_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer border border-slate-200" data-testid="branding-sidebar-color" />
              <Input value={branding.sidebar_color} onChange={e => setBranding({...branding, sidebar_color: e.target.value})} className="h-8 text-sm font-mono flex-1" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Fond du menu lateral</p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div>
        <Label className="text-xs text-slate-500 mb-2 block">Aperçu</Label>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 h-32">
          <div className="w-48 p-3 flex flex-col justify-between" style={{ backgroundColor: branding.sidebar_color }}>
            <div>
              {branding.logo_url ? (
                <img src={`${BACKEND_URL}${branding.logo_url}`} alt="" className="h-6 object-contain mb-2" />
              ) : (
                <p className="text-white text-sm font-semibold mb-2">{branding.app_name || "KomptAI"}</p>
              )}
              <div className="space-y-1">
                <div className="h-2 w-20 rounded" style={{ backgroundColor: branding.accent_color, opacity: 0.8 }} />
                <div className="h-2 w-16 rounded bg-white/20" />
                <div className="h-2 w-24 rounded bg-white/20" />
              </div>
            </div>
            <div className="h-2 w-12 rounded bg-white/30" />
          </div>
          <div className="flex-1 bg-slate-50 p-3">
            <div className="h-3 w-32 rounded mb-2" style={{ backgroundColor: branding.primary_color }} />
            <div className="flex gap-2">
              <div className="h-6 px-3 rounded text-white text-[10px] flex items-center" style={{ backgroundColor: branding.primary_color }}>Bouton</div>
              <div className="h-6 px-3 rounded text-white text-[10px] flex items-center" style={{ backgroundColor: branding.accent_color }}>Accent</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-branding-btn">
          <Save size={16} className="mr-2" /> Enregistrer la personnalisation
        </Button>
        {!isDefault && (
          <Button variant="outline" onClick={resetAll} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid="reset-branding-btn">
            <RotateCcw size={14} className="mr-1" /> Reinitialiser tout
          </Button>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN ADMIN PAGE ====================
export default function AdminPage({ user }) {
  const isSuperadmin = user?.is_superadmin || false;
  const tenantRole = user?.tenant_role;
  const isAdmin = isSuperadmin || tenantRole === "owner" || tenantRole === "admin";
  const modules = user?.modules || [];
  const hasMarqueBlanche = modules.includes("marque_blanche");

  return (
    <div className="page-container" data-testid="admin-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Administration</h1>
        <p className="text-slate-500 mt-1">Paramètres, accès et configuration</p>
      </div>
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <Tabs defaultValue="company">
          <TabsList className="mb-6 h-auto flex-wrap">
            <TabsTrigger value="company" data-testid="tab-company"><Settings size={16} className="mr-2" />Société</TabsTrigger>
            {isAdmin && <TabsTrigger value="users" data-testid="tab-users"><Users size={16} className="mr-2" />Utilisateurs</TabsTrigger>}
            {isAdmin && <TabsTrigger value="modules" data-testid="tab-modules"><Lock size={16} className="mr-2" />Modules</TabsTrigger>}
            {isAdmin && hasMarqueBlanche && <TabsTrigger value="branding" data-testid="tab-branding"><Palette size={16} className="mr-2" />Personnalisation</TabsTrigger>}
            {isSuperadmin && <TabsTrigger value="tenants" data-testid="tab-tenants"><Building2 size={16} className="mr-2" />Sociétés clientes</TabsTrigger>}
            {isSuperadmin && <TabsTrigger value="all-users" data-testid="tab-all-users"><Users size={16} className="mr-2" />Tous les utilisateurs</TabsTrigger>}
          </TabsList>
          <TabsContent value="company"><CompanyTab modules={modules} /></TabsContent>
          <TabsContent value="users"><TenantUsersTab user={user} /></TabsContent>
          <TabsContent value="modules"><ModulesTab user={user} /></TabsContent>
          <TabsContent value="branding"><BrandingTab /></TabsContent>
          <TabsContent value="tenants"><TenantsTab /></TabsContent>
          <TabsContent value="all-users"><AllUsersTab /></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
